import { is, mergeDeepLeft, omit } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { warn } from './log'

export const getLocalStorageItem = (key: string) => {
  const item = localStorage.getItem(key)
  try {
    const jsonItem = JSON.parse(item!)
    return jsonItem
  } catch (e) {
    return item
  }
}

export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const getInitialValue = useCallback(() => {
    try {
      const item = getLocalStorageItem(key)

      return item || initialValue
    } catch (error) {
      return initialValue
    }
  }, [initialValue, key])

  const [storedValue, setStoredValue] = useState(getInitialValue)

  const setValue = (value: T | ((x: T) => T), stringify = true) => {
    try {
      const valueToStore = is(Function, value) ? value(storedValue) : value

      setStoredValue(valueToStore)
      localStorage.setItem(key, stringify ? JSON.stringify(valueToStore) : (valueToStore as string))
    } catch (error) {
      warn(error)
    }
  }

  const clear = () => {
    localStorage.removeItem(key)
    setValue(initialValue)
  }

  const invalidate = () => {
    setValue(getInitialValue())
  }

  return [storedValue, setValue, clear, invalidate]
}

export const useLocalStorageObject = <T>({
  parent,
  key,
  initialValue,
}: {
  parent: string
  key: string
  initialValue: T
}) => {
  const [object, setObject] = useLocalStorage<Record<string, T>>(parent, {})

  const value = useMemo(() => object[key] || initialValue, [key, object])

  const setValue = useCallback(
    (newValue: T) => {
      setObject(mergeDeepLeft({ [key]: newValue }))
    },
    [key],
  )
  const clearValue = useCallback(() => setObject(omit([key])), [key])

  return {
    value,
    setValue,
    clearValue,
  }
}
